import type { CaptureResult } from "posthog-js";
import { INJECTED_SCRIPT_EXCEPTION_PATTERNS } from "./constants";

/** The part of a `$exception_list` entry this filter reads. */
type ExceptionEntry = { value?: unknown };

/**
 * PostHog `before_send` hook that drops `$exception` events thrown by scripts a host browser or
 * app injected (see {@link INJECTED_SCRIPT_EXCEPTION_PATTERNS}). An event is dropped only when
 * every exception in it matches a pattern, so a real error is never hidden behind a bridge error.
 * Every other event passes through untouched.
 *
 * @param event - The event PostHog is about to send, or null if an earlier hook dropped it.
 * @returns The event unchanged, or null to drop it.
 */
export function dropInjectedScriptExceptions(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== "$exception") return event;

  const exceptionList: ExceptionEntry[] | undefined = event.properties.$exception_list;
  if (!Array.isArray(exceptionList) || exceptionList.length === 0) return event;

  const isInjectedNoise = exceptionList.every(
    ({ value }) =>
      typeof value === "string" && INJECTED_SCRIPT_EXCEPTION_PATTERNS.some((pattern) => pattern.test(value))
  );

  return isInjectedNoise ? null : event;
}
